import { useFormAugmentedContext } from '@framework/form';
import { get } from 'lodash';
import { useMemo } from 'react';
import { debug } from '../../../../utils/debug';
import { IMaterializedReference } from '../../evaluate/materializeReferencePath';
import { partitionReferences } from '../../evaluate/partitionReferences';
import {
  IEvaluationVariables,
  createEvaluationVariables,
} from '../../evaluate/variables/IEvaluationVariables';
import { useWatchFormReferences } from './useWatchFormReferences';

export interface IUseWatchReferencesOptions {
  references: IMaterializedReference[];
}

/**
 * Hook per accedere a un _qualsiasi_ riferimento (`IReference`) a partire
 * da un Widget. In base al tipo di riferimento, il valore può essere
 * recuperato da:
 * - `useWatchFormReferences` per i riferimenti interni al form
 * - `useFormAugmentedContext` per i riferimenti al contesto
 *
 * Questa logica è sostanzialmente la replica di `parentWhen`, ma per
 * essere utilizzata in `useExpressionValue` ha bisogno di essere
 * collegata al form di RHF.
 *
 * @param references Riferimenti da cui recuperare i valori. Essendo `materialized`,
 *  Non abbiamo bisogno di passare il nome del campo, perché è già calcolato il path
 *  assoluto.
 */
export function useWatchReferences(
  references: IMaterializedReference[],
): IEvaluationVariables {
  const augmented = useFormAugmentedContext();

  const [contextRefs, formRefs] = useMemo(
    () => partitionReferences(references),
    [references],
  );

  // 1. Valori del form (`useWatch`)
  const formValues = useWatchFormReferences(formRefs);

  // 2. Valori del contesto (`useFormAugmentedContext`)
  const contextValues = useMemo(
    () =>
      contextRefs.map(ref =>
        get(augmented.validationContext, ref.absolutePath.substring(1)),
      ),
    [augmented.validationContext, contextRefs],
  );

  // Uniamo i valori
  return useMemo(() => {
    debug.log('expression.watch', 'useWatchReferences', { formRefs, formValues }); // prettier-ignore
    return createEvaluationVariables(
      [...contextRefs, ...formRefs],
      [...contextValues, ...formValues],
    );
  }, [formValues, contextValues, contextRefs, formRefs]);
}
